import { useEffect, useState, useCallback } from 'react';
import { Server, Shield, ExternalLink, Check, ArrowRight, Sparkles, Layers, Globe, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Alert } from '../components/Alert';
import { Loader } from '../components/Loader';
import { Dialog } from '../components/Dialog';
import { Input } from '../components/Input';
import { CatalogPlan, DomainSearchResult, OrgCatalogue, OrgProduct, PriceInterval } from '../types';

// The dashboard is in English; plan names and features come from the catalogue
// in the same language so a page never mixes the two.
const LOCALE = 'en';

const PRODUCT_ICONS: Record<string, typeof Shield> = { intel: Shield, hosting: Server, domains: Globe };

const DOMAINS = 'domains';

const STATUS_LABEL: Record<string, { label: string; tone: 'success' | 'warning' | 'danger' | 'accent' }> = {
  trialing: { label: 'Trial', tone: 'accent' },
  active: { label: 'Active', tone: 'success' },
  past_due: { label: 'Payment due', tone: 'warning' },
  suspended: { label: 'Suspended', tone: 'danger' },
};

function money(minor: number, currency: string): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: minor % 100 === 0 ? 0 : 2,
  }).format(minor / 100);
}

const INTERVALS: PriceInterval[] = ['monthly', 'quarterly', 'yearly'];
const INTERVAL_LABEL: Record<PriceInterval, string> = { monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };
const PER: Record<PriceInterval, string> = { monthly: 'month', quarterly: 'quarter', yearly: 'year' };

function priceFor(plan: CatalogPlan, interval: PriceInterval) {
  return plan.prices.find((p) => p.interval === interval) || plan.prices[0];
}

type OfferedPlan = CatalogPlan & { checkout_url?: string };

function checkoutUrl(plan: OfferedPlan, interval: PriceInterval, orgId: string, quantity = 1): string {
  const params = new URLSearchParams({ plan: plan.code, org_id: orgId, lang: LOCALE });
  if (plan.checkout_url) {
    const offer = new URL(plan.checkout_url, window.location.origin).searchParams.get('offer');
    if (offer) params.set('offer', offer);
  }
  if (!plan.is_free && plan.prices.some((p) => p.interval === interval)) params.set('interval', interval);
  if (quantity > 1) params.set('quantity', String(quantity));
  return `/checkout?${params.toString()}`;
}

/** What one unit is called on products bought by quantity. */
const UNIT: Record<string, [string, string]> = { hosting: ['website', 'websites'] };

function unitsLabel(product: string, n: number): string {
  const [one, many] = UNIT[product] || ['unit', 'units'];
  return `${n} ${n === 1 ? one : many}`;
}

function ProductIcon({ code, size = 22 }: { code: string; size?: number }) {
  const Icon = PRODUCT_ICONS[code] || Layers;
  return (
    <div
      style={{
        width: size * 2,
        height: size * 2,
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-inset)',
        border: '1px solid var(--border-subtle)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--accent)',
        flexShrink: 0,
      }}
    >
      <Icon size={size} />
    </div>
  );
}

function ActiveProductCard({
  product,
  canPurchase,
  onChoosePlan,
}: {
  product: OrgProduct;
  canPurchase: boolean;
  onChoosePlan: () => void;
}) {
  const sub = product.subscription;
  const status = sub ? STATUS_LABEL[sub.status] : null;
  const multi = product.multi_plan && product.subscriptions.length > 0;
  // Changing a paid plan happens on the Billing page; from Free it is an ordinary checkout.
  // Multi-plan products take another tier here, next to the ones held.
  const canUpgrade =
    canPurchase &&
    (product.multi_plan || !!sub?.is_free) &&
    (product.plans.some((p) => !p.is_free) || product.offers.length > 0);

  return (
    <Card padding="var(--space-6)" glow>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <ProductIcon code={product.code} size={24} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>{product.name}</h2>
            {status && (
              <Badge tone={status.tone} dot>
                {status.label}
              </Badge>
            )}
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 4, lineHeight: 1.5 }}>
            {product.tagline}
          </p>
        </div>
      </div>

      {multi ? (
        <div
          style={{
            margin: '20px 0',
            padding: '14px 16px',
            background: 'var(--bg-inset)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-subtle)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            fontSize: 'var(--text-sm)',
          }}
        >
          {product.subscriptions.map((s, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-semibold)' }}>
                {s.plan_name}{' '}
                <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>· {unitsLabel(product.code, s.quantity)}</span>
              </span>
              <span style={{ color: 'var(--text-tertiary)' }}>
                {s.comped
                  ? 'Included'
                  : s.is_free
                    ? 'Free'
                    : `${money(s.amount_minor * s.quantity, s.currency)} / ${PER[s.interval as PriceInterval] || 'month'}`}
              </span>
            </div>
          ))}
          <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', marginTop: 4 }}>
            {unitsLabel(product.code, product.subscriptions.reduce((n, s) => n + s.quantity, 0))} in total. Add{' '}
            {UNIT[product.code]?.[1] || 'units'} or change a plan from{' '}
            <a href="/billing" style={{ color: 'var(--accent)' }}>
              Billing
            </a>
            .
          </div>
        </div>
      ) : (
      <div
        style={{
          margin: '20px 0',
          padding: '14px 16px',
          background: 'var(--bg-inset)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-subtle)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 12,
          fontSize: 'var(--text-sm)',
        }}
      >
        <div>
          <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>Plan</div>
          <div style={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-semibold)', marginTop: 2 }}>
            {sub ? sub.plan_name : 'Included'}
          </div>
        </div>
        {sub && (
          <div>
            <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>Price</div>
            <div style={{ color: 'var(--text-primary)', marginTop: 2 }}>
              {sub.is_free
                ? 'Free'
                : `${money(sub.amount_minor, sub.currency)} / ${PER[sub.interval as PriceInterval] || 'month'}`}
            </div>
          </div>
        )}
        {sub && !sub.is_free && (
          <div>
            <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
              {sub.cancel_at_period_end ? 'Ends on' : 'Renews on'}
            </div>
            <div style={{ color: 'var(--text-primary)', marginTop: 2 }}>
              {new Date(sub.current_period_end).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </div>
          </div>
        )}
      </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {canUpgrade && (
          <Button variant="secondary" onClick={onChoosePlan} icon={<Sparkles size={14} />}>
            {product.multi_plan && !sub?.is_free ? 'Add a plan' : 'Upgrade'}
          </Button>
        )}
        <a
          href={product.launch_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '9px 16px',
            background: 'var(--accent)',
            color: 'var(--text-on-accent)',
            borderRadius: 'var(--radius-md)',
            fontWeight: 500,
            fontSize: 'var(--text-sm)',
          }}
        >
          Open {product.name} <ExternalLink size={14} />
        </a>
      </div>
    </Card>
  );
}

const DATE = { day: 'numeric', month: 'short', year: 'numeric' } as const;

/** Domain names: one subscription per name, priced from the registrar when searched. */
function DomainsCard({ product, canPurchase, orgId }: { product: OrgProduct; canPurchase: boolean; orgId: string }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<DomainSearchResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const search = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = q.trim().toLowerCase();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      // A bare name is tried with the usual extensions.
      const names = name.includes('.') ? [name] : ['fr', 'com', 'eu', 'net', 'org'].map((tld) => `${name}.${tld}`);
      setResults(await api.checkout.searchDomains(names.join(',')));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setBusy(false);
    }
  };

  const order = (r: DomainSearchResult) => {
    const params = new URLSearchParams({ plan: 'domains.registration', domain: r.domain, org_id: orgId, lang: LOCALE });
    window.location.href = `/checkout?${params.toString()}`;
  };

  return (
    <Card padding="var(--space-6)" glow={product.subscriptions.length > 0}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <ProductIcon code={product.code} size={24} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>{product.name}</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 4, lineHeight: 1.5 }}>
            {product.tagline} DNS is managed in Hosting.
          </p>
        </div>
      </div>

      {product.subscriptions.length > 0 && (
        <div style={{ margin: '20px 0 0', padding: '14px 16px', background: 'var(--bg-inset)', borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 'var(--text-sm)' }}>
          {product.subscriptions.map((s) => (
            <div key={s.domain_name || s.plan_code} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                {s.domain_name}{' '}
                {STATUS_LABEL[s.status] && s.status !== 'active' && (
                  <Badge tone={STATUS_LABEL[s.status].tone}>{STATUS_LABEL[s.status].label}</Badge>
                )}
              </span>
              <span style={{ color: 'var(--text-tertiary)' }}>
                {s.comped ? 'Included' : `${money(s.amount_minor, s.currency)} / year`} ·{' '}
                {s.cancel_at_period_end ? 'ends' : s.comped ? 'renewed' : 'renews'}{' '}
                {new Date(s.current_period_end).toLocaleDateString('en-GB', DATE)}
              </span>
            </div>
          ))}
        </div>
      )}

      {canPurchase && (
        <form onSubmit={search} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 20 }}>
          <div style={{ flex: 1 }}>
            <Input label="Find a domain name" placeholder="acme.fr" value={q} mono onChange={(e) => setQ(e.target.value)} />
          </div>
          <Button type="submit" variant="secondary" icon={<Search size={14} />} disabled={busy || !q.trim()}>
            Search
          </Button>
        </form>
      )}
      {error && <Alert tone="warning" style={{ marginTop: 12 }}>{error}</Alert>}
      {results && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 'var(--text-sm)' }}>
          {results.length === 0 && <span style={{ color: 'var(--text-tertiary)' }}>Not a valid domain name.</span>}
          {results.map((r) => (
            <div key={r.domain} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
              padding: '8px 12px', background: 'var(--bg-inset)', borderRadius: 'var(--radius-md)' }}>
              <span style={{ fontFamily: 'var(--font-mono)' }}>{r.domain}</span>
              {r.price_minor !== null ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span>
                    {money(r.price_minor, r.currency)} / year
                    {r.renew_price_minor !== null && r.renew_price_minor !== r.price_minor && (
                      <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
                        {' '}· then {money(r.renew_price_minor, r.currency)}
                      </span>
                    )}
                  </span>
                  <Button size="sm" onClick={() => order(r)} icon={<ArrowRight size={14} />}>Order</Button>
                </span>
              ) : (
                <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
                  {r.taken_here ? 'Already with CYRN' : !r.available ? 'Taken' : 'Not sold here'}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function AvailableProductCard({
  product,
  canPurchase,
  onChoosePlan,
}: {
  product: OrgProduct;
  canPurchase: boolean;
  onChoosePlan: () => void;
}) {
  const onSale = product.plans.length > 0;
  const hasFree = product.plans.some((p) => p.is_free);
  const cheapest = product.plans
    .filter((p) => !p.is_free)
    .flatMap((p) => p.prices.filter((pr) => pr.interval === 'monthly'))
    .sort((a, b) => a.amount_minor - b.amount_minor)[0];

  return (
    <Card padding="var(--space-5)" style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <ProductIcon code={product.code} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--fw-semibold)' }}>{product.name}</h3>
            {!onSale && <Badge tone="neutral">Coming soon</Badge>}
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 4, lineHeight: 1.5 }}>
            {product.tagline}
          </p>
        </div>
      </div>

      <div
        style={{
          marginTop: 'auto',
          paddingTop: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
          {onSale
            ? hasFree
              ? `Free plan available${cheapest ? `, paid from ${money(cheapest.amount_minor, cheapest.currency)} / month` : ''}`
              : cheapest
                ? `From ${money(cheapest.amount_minor, cheapest.currency)} / month`
                : ''
            : 'Not available to order yet'}
        </span>
        {onSale && (
          <Button
            variant="primary"
            size="sm"
            onClick={onChoosePlan}
            disabled={!canPurchase}
            icon={<ArrowRight size={14} />}
          >
            See plans
          </Button>
        )}
      </div>
    </Card>
  );
}

function PlanPicker({
  product,
  orgId,
  onClose,
}: {
  product: OrgProduct | null;
  orgId: string;
  onClose: () => void;
}) {
  const [chosen, setBillingInterval] = useState<PriceInterval | null>(null);
  const [quantity, setQuantity] = useState(1);
  if (!product) return null;

  const multi = product.multi_plan;
  // On multi-plan products every tier held is "current": more units are added from Billing.
  const held = new Set(
    multi ? product.subscriptions.filter((s) => !s.comped).map((s) => s.plan_code) : [product.subscription?.plan_code],
  );
  const base: OfferedPlan[] = product.subscription ? product.plans.filter((p) => !p.is_free) : product.plans;
  const plans: OfferedPlan[] = [...product.offers, ...base];
  const offered = INTERVALS.filter((iv) => plans.some((p) => !p.is_free && p.prices.some((pr) => pr.interval === iv)));
  const interval: PriceInterval =
    chosen && offered.includes(chosen) ? chosen : offered.includes('yearly') ? 'yearly' : offered[0] || 'monthly';

  return (
    <Dialog open title={`Choose a ${product.name} plan`} onClose={onClose} maxWidth={980}>
      {offered.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <div
            style={{
              display: 'inline-flex',
              padding: 4,
              background: 'var(--bg-inset)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-subtle)',
              gap: 4,
            }}
          >
            {offered.map((iv) => (
              <button
                key={iv}
                type="button"
                onClick={() => setBillingInterval(iv)}
                style={{
                  padding: '6px 16px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 500,
                  background: interval === iv ? 'var(--accent)' : 'transparent',
                  color: interval === iv ? 'var(--text-on-accent)' : 'var(--text-secondary)',
                }}
              >
                {INTERVAL_LABEL[iv]}
              </button>
            ))}
          </div>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(200px, 1fr))`,
          gap: 14,
        }}
      >
        {plans.map((plan) => {
          const price = priceFor(plan, interval);
          const isCurrent = held.has(plan.code);
          return (
            <div
              key={plan.code}
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: 18,
                borderRadius: 'var(--radius-lg)',
                background: 'var(--bg-surface)',
                border: plan.is_featured ? '1px solid var(--accent)' : '1px solid var(--border-subtle)',
                boxShadow: plan.is_featured ? '0 0 0 1px var(--accent-soft)' : undefined,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span
                  style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 'var(--text-md)', color: 'var(--text-primary)' }}
                >
                  {plan.name}
                </span>
                {plan.checkout_url ? (
                  <Badge tone="accent">Offered to you</Badge>
                ) : (
                  plan.is_featured && <Badge tone="accent">Popular</Badge>
                )}
              </div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: 4, minHeight: 32 }}>
                {plan.tagline}
              </div>
              <div style={{ margin: '12px 0', color: 'var(--text-primary)' }}>
                {plan.is_free || !price ? (
                  <span style={{ fontSize: 'var(--text-2xl)', fontWeight: 700 }}>Free</span>
                ) : (
                  <>
                    <span style={{ fontSize: 'var(--text-2xl)', fontWeight: 700 }}>
                      {money(price.amount_minor, price.currency)}
                    </span>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
                      {' '}
                      / {PER[price.interval]}
                    </span>
                  </>
                )}
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {plan.features.map((f) => (
                  <li key={f} style={{ display: 'flex', gap: 8, fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                    <Check size={14} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div style={{ marginTop: 'auto' }}>
                {isCurrent && multi ? (
                  <Button
                    variant="secondary"
                    style={{ width: '100%', justifyContent: 'center' }}
                    onClick={() => {
                      window.location.href = '/billing';
                    }}
                  >
                    Add {UNIT[product.code]?.[1] || 'units'} in Billing
                  </Button>
                ) : isCurrent ? (
                  <Button variant="secondary" disabled style={{ width: '100%' }}>
                    Current plan
                  </Button>
                ) : (
                  <Button
                    variant={plan.is_featured ? 'primary' : 'secondary'}
                    style={{ width: '100%', justifyContent: 'center' }}
                    onClick={() => {
                      window.location.href = checkoutUrl(plan, interval, orgId, multi && !plan.is_free ? quantity : 1);
                    }}
                  >
                    {plan.is_free ? 'Start for free' : `Choose ${plan.name}`}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {multi && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            marginTop: 18,
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
          }}
        >
          <label htmlFor="plan-quantity">Number of {UNIT[product.code]?.[1] || 'units'}</label>
          <input
            id="plan-quantity"
            type="number"
            min={1}
            max={100}
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, Math.min(100, parseInt(e.target.value || '1', 10) || 1)))}
            style={{
              width: 72,
              padding: '6px 8px',
              background: 'var(--bg-inset)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-primary)',
            }}
          />
          <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
            Prices are per {UNIT[product.code]?.[0] || 'unit'}. You can mix plans.
          </span>
        </div>
      )}
      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: 16, textAlign: 'center' }}>
        Prices include all taxes. You can review everything before paying.
      </p>
    </Dialog>
  );
}

export function ProductsPage() {
  const { activeOrg, activeRole } = useAuth();
  const [catalogue, setCatalogue] = useState<OrgCatalogue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [choosing, setChoosing] = useState<OrgProduct | null>(null);

  const isNormalMember = activeRole === 'member' || activeRole === 'readonly';

  const load = useCallback(async () => {
    if (!activeOrg?.id) return;
    try {
      setCatalogue(await api.orgs.catalogue(activeOrg.id, LOCALE));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [activeOrg?.id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <Loader label="Loading products..." />;
  }

  const domains = catalogue?.products.find((p) => p.code === DOMAINS);
  const apps = catalogue?.products.filter((p) => p.code !== DOMAINS) ?? [];
  const active = apps.filter((p) => p.active);
  const available = apps.filter((p) => !p.active);
  const canPurchase = !!catalogue?.can_purchase;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-8)' }}>
      <div>
        <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--fw-bold)' }}>Products</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 4 }}>
          CYRN products used by <strong style={{ color: 'var(--text-primary)' }}>{activeOrg?.display_name}</strong>, and
          the ones you can add.
        </p>
      </div>

      {error && (
        <Alert tone="danger" title="Error">
          {error}
        </Alert>
      )}

      <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>Your products</h2>
        {active.length === 0 ? (
          <Card padding="var(--space-6)">
            <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
              {isNormalMember
                ? "You do not have access to any CYRN applications in this organisation. Please contact an organisation administrator to be granted access."
                : "This organisation doesn't use any CYRN product yet. Pick one below to get started."}
            </div>
          </Card>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 'var(--space-5)' }}>
            {active.map((p) => (
              <ActiveProductCard key={p.code} product={p} canPurchase={canPurchase} onChoosePlan={() => setChoosing(p)} />
            ))}
          </div>
        )}
      </section>

      {domains && activeOrg?.id && (domains.subscriptions.length > 0 || canPurchase) && (
        <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>Domain names</h2>
          <DomainsCard product={domains} canPurchase={canPurchase} orgId={activeOrg.id} />
        </section>
      )}

      {!isNormalMember && available.length > 0 && (
        <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div>
            <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>Available products</h2>
            {!canPurchase && (
              <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', marginTop: 4 }}>
                Only the organisation's owner or billing contact can add a product.
              </p>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 'var(--space-4)' }}>
            {available.map((p) => (
              <AvailableProductCard key={p.code} product={p} canPurchase={canPurchase} onChoosePlan={() => setChoosing(p)} />
            ))}
          </div>
        </section>
      )}

      {activeOrg?.id && <PlanPicker product={choosing} orgId={activeOrg.id} onClose={() => setChoosing(null)} />}
    </div>
  );
}
